'use client';

import { Box, Typography } from '@mui/material';

import { Product } from '../../../services/types/product';
import ProductCard from '../../molecules/ProductCard';

export interface ProductGridProps {
  products: Product[];
  title?: string;
  subtitle?: string;
  variant?: 'default' | 'featured' | 'compact';
  loading?: boolean;
}

const ProductGrid = ({
  products,
  title,
  subtitle,
  variant = 'default',
  loading = false,
}: ProductGridProps) => {
  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <Box key={index}>
              <Box
                sx={{
                  height: 400,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (!products || products.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant='h6' color='text.secondary' gutterBottom>
          Không có sản phẩm nào
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Vui lòng thử lại sau hoặc liên hệ với chúng tôi để được hỗ trợ.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      {(title || subtitle) && (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          {title && (
            <Typography
              variant='h4'
              component='h2'
              sx={{
                fontWeight: 700,
                color: '#1A1A1A',
                marginBottom: subtitle ? 1 : 3,
                fontSize: { xs: '1.75rem', md: '2.125rem' },
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{
                maxWidth: 600,
                margin: '0 auto',
                lineHeight: 1.6,
                fontSize: { xs: '1rem', md: '1.125rem' },
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 3,
        }}
      >
        {products.map(product => (
          <Box key={product.id}>
            <ProductCard product={product} variant={variant} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ProductGrid;
